/**
 * O ícone de onde o item abre — Teams, Microsoft 365, Snowflake, site próprio.
 *
 * ## O destino sai da URL, não de um campo
 *
 * Nenhum cadastro escolhe o ícone: ele é deduzido do endereço. Um campo
 * "plataforma de destino" no painel de administração seria mais uma coisa para
 * alguém preencher errado — e a URL já diz a verdade, porque é ela que abre.
 * Trocar o link troca o ícone junto, sem ninguém lembrar de fazer isso.
 *
 * ## Por que SVG embutido, e não arquivo
 *
 * A CSP é `img-src 'self' data:`: buscar o logotipo no site do fornecedor seria
 * bloqueado. Hospedar quatro PNGs resolveria, mas SVG no JSX escala sem borrar,
 * acompanha a cor do texto quando é monocromático e não custa requisição.
 *
 * ## As marcas são simplificadas de propósito
 *
 * São reconhecíveis a 16px, que é o tamanho em que aparecem. Reproduzir a marca
 * oficial em detalhe num ícone desse tamanho não muda o que a pessoa enxerga, e
 * marca de terceiro mantém as cores originais — recolorir logotipo alheio é o
 * tipo de coisa que o jurídico pergunta depois.
 */

/** Domínio → destino. A ordem importa: o primeiro que casar vence. */
const HOSTS = [
  [/(^|\.)teams\.(microsoft\.com|cloud\.microsoft)$/, 'teams'],
  [/(^|\.)snowflake(computing)?\.com$/, 'snowflake'],
  [/(^|\.)powerbi\.com$/, 'powerbi'],
  [
    /(^|\.)(m365\.cloud\.microsoft|microsoft365\.com|office\.com|office365\.com|copilot\.microsoft\.com|copilotstudio\.microsoft\.com|sharepoint\.com|outlook\.(office|office365)\.com)$/,
    'm365',
  ],
];

/**
 * Onde este endereço abre.
 *
 * URL inválida cai em `externo` em vez de estourar: o valor vem do banco, e uma
 * linha antiga malformada não pode derrubar o portfólio inteiro.
 */
export function destinationOf(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    for (const [padrao, id] of HOSTS) if (padrao.test(host)) return id;
    return 'externo';
  } catch {
    return 'externo';
  }
}

/** Nome do destino, para o rótulo automático e para o leitor de tela. */
export const DESTINATION_NAMES = Object.freeze({
  teams: 'Microsoft Teams',
  m365: 'Microsoft 365',
  snowflake: 'Snowflake',
  powerbi: 'Power BI',
  externo: 'site do fornecedor',
});

/**
 * Todos os lugares onde um item abre: o principal e os alternativos.
 *
 * Um só lugar monta essa lista, porque o cartão do portfólio e o painel de
 * detalhe precisam da mesma — e duas montagens divergiriam no dia em que
 * alguém acrescentasse uma regra numa e esquecesse a outra.
 *
 * Item sem permissão de abrir devolve lista vazia: o servidor já não manda
 * `targetUrl` nem `links` nesse caso, e a checagem aqui é a segunda tranca.
 */
export function destinationsOf(item) {
  if (!item?.access?.canOpen) return [];

  const out = [];

  if (item.targetUrl) {
    out.push({
      url: item.targetUrl,
      // O botão principal fala do item, não do destino: "Abrir Comercial" é o
      // que a pessoa procura. O nome do lugar fica no ícone e no `title`.
      label: `Abrir ${item.shortName ?? item.name}`,
      where: DESTINATION_NAMES[destinationOf(item.targetUrl)],
      primary: true,
    });
  }

  for (const l of item.links ?? []) {
    if (!l?.url) continue;
    out.push({
      url: l.url,
      label: l.label || `Abrir no ${DESTINATION_NAMES[destinationOf(l.url)]}`,
      where: l.label || DESTINATION_NAMES[destinationOf(l.url)],
      primary: false,
    });
  }

  return out;
}

/* ── as marcas ───────────────────────────────────────────────── */

function Teams() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="4 4 36 38">
      <path
        fill="url(#a)"
        d="M21.9999 20h12c3.3137 0 6 2.6863 6 6v10c0 3.3137-2.6863 6-6 6s-6-2.6863-6-6V26c0-3.3137-2.6863-6-6-6"
      />
      <path
        fill="url(#b)"
        d="M7.99988 24c0-3.3137 2.68632-6 6.00002-6h8c3.3137 0 6 2.6863 6 6v12c0 3.3137 2.6863 6 6 6l-16.0001-.0001c-5.5228 0-9.99992-4.4771-9.99992-10z"
      />
      <path
        fill="url(#c)"
        fillOpacity=".7"
        d="M7.99988 24c0-3.3137 2.68632-6 6.00002-6h8c3.3137 0 6 2.6863 6 6v12c0 3.3137 2.6863 6 6 6l-16.0001-.0001c-5.5228 0-9.99992-4.4771-9.99992-10z"
      />
      <path
        fill="url(#d)"
        fillOpacity=".7"
        d="M7.99988 24c0-3.3137 2.68632-6 6.00002-6h8c3.3137 0 6 2.6863 6 6v12c0 3.3137 2.6863 6 6 6l-16.0001-.0001c-5.5228 0-9.99992-4.4771-9.99992-10z"
      />
      <path
        fill="url(#e)"
        d="M32.9999 18c2.7614 0 5-2.2386 5-5s-2.2386-5-5-5-5 2.2386-5 5 2.2386 5 5 5"
      />
      <path
        fill="url(#f)"
        fillOpacity=".46"
        d="M32.9999 18c2.7614 0 5-2.2386 5-5s-2.2386-5-5-5-5 2.2386-5 5 2.2386 5 5 5"
      />
      <path
        fill="url(#g)"
        fillOpacity=".4"
        d="M32.9999 18c2.7614 0 5-2.2386 5-5s-2.2386-5-5-5-5 2.2386-5 5 2.2386 5 5 5"
      />
      <path
        fill="url(#h)"
        d="M17.9999 16c3.3137 0 6-2.6863 6-6 0-3.31371-2.6863-6-6-6s-6 2.68629-6 6c0 3.3137 2.6863 6 6 6"
      />
      <path
        fill="url(#i)"
        fillOpacity=".6"
        d="M17.9999 16c3.3137 0 6-2.6863 6-6 0-3.31371-2.6863-6-6-6s-6 2.68629-6 6c0 3.3137 2.6863 6 6 6"
      />
      <path
        fill="url(#j)"
        fillOpacity=".5"
        d="M17.9999 16c3.3137 0 6-2.6863 6-6 0-3.31371-2.6863-6-6-6s-6 2.68629-6 6c0 3.3137 2.6863 6 6 6"
      />
      <rect width="16" height="16" x="4" y="23" fill="url(#k)" rx="3.25" />
      <rect width="16" height="16" x="4" y="23" fill="url(#l)" fillOpacity=".7" rx="3.25" />
      <path
        fill="#fff"
        d="M15.4792 28.1054h-2.4471v7.466h-2.0648v-7.466H8.52014v-1.6768h6.95906z"
      />
      <defs>
        <radialGradient
          id="a"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(13.4784 0 0 33.2694 39.7967 22.1739)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#a98aff" />
          <stop offset=".14" stopColor="#8c75ff" />
          <stop offset=".565" stopColor="#5f50e2" />
          <stop offset=".9" stopColor="#3c2cb8" />
        </radialGradient>
        <radialGradient
          id="b"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(68.1539 -7.71566095 14.71355834)scale(32.752 33.1231)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#85c2ff" />
          <stop offset=".69" stopColor="#7588ff" />
          <stop offset="1" stopColor="#6459fe" />
        </radialGradient>
        <radialGradient
          id="d"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(113.326 8.09285255 17.64474501)scale(19.2186 15.4273)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#bd96ff" />
          <stop offset=".686685" stopColor="#bd96ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="e"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(0 -10 12.6216 0 32.9999 11.5714)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".268201" stopColor="#6868f7" />
          <stop offset="1" stopColor="#3923b1" />
        </radialGradient>
        <radialGradient
          id="f"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(40.0516 -.03068196 44.8729095)scale(7.14629 10.3363)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".270711" stopColor="#a1d3ff" />
          <stop offset=".813393" stopColor="#a1d3ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="g"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(-41.6581 32.11799918 -43.41948423)scale(8.51275 20.8824)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#e3acfd" />
          <stop offset=".816041" stopColor="#9fa2ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="h"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(0 -12 15.146 0 17.9999 8.28571)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".268201" stopColor="#8282ff" />
          <stop offset="1" stopColor="#3923b1" />
        </radialGradient>
        <radialGradient
          id="i"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(40.0516 -3.15465147 21.41641466)scale(8.57554 12.4035)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".270711" stopColor="#a1d3ff" />
          <stop offset=".813393" stopColor="#a1d3ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="j"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(-41.6581 20.38180375 -26.51566158)scale(10.2153 25.0589)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#e3acfd" />
          <stop offset=".816041" stopColor="#9fa2ff" stopOpacity="0" />
        </radialGradient>
        <radialGradient
          id="k"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="rotate(45 -25.76345597 16.32842712)scale(22.6274)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".046875" stopColor="#688eff" />
          <stop offset=".946875" stopColor="#230f94" />
        </radialGradient>
        <radialGradient
          id="l"
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(0 11.2 -13.0702 0 12 32.6)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".570647" stopColor="#6965f6" stopOpacity="0" />
          <stop offset="1" stopColor="#8f8fff" />
        </radialGradient>
        <linearGradient
          id="c"
          x1="20.5936"
          x2="20.5936"
          y1="18"
          y2="42"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".801159" stopColor="#6864f6" stopOpacity="0" />
          <stop offset="1" stopColor="#5149de" />
        </linearGradient>
      </defs>
    </svg>
  );
}

/** O quadriculado da Microsoft. Mesmas cores do botão de entrada. */
function Microsoft365() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
      <path
        d="M34.1423 7.32501C33.5634 5.35387 31.7547 4 29.7003 4L28.3488 4C26.1142 4 24.1985 5.59611 23.7952 7.79398L21.4805 20.4072L22.0549 18.4419C22.6319 16.4679 24.4419 15.1111 26.4986 15.1111H34.3524L37.6462 16.3942L40.8213 15.1111H39.8946C37.8401 15.1111 36.0315 13.7572 35.4525 11.7861L34.1423 7.32501Z"
        fill="url(#paint0_radial_56201_15503)"
      />
      <path
        d="M14.3307 40.656C14.9032 42.6366 16.7165 44 18.7783 44H21.6486C24.1592 44 26.2122 41.999 26.2767 39.4893L26.5893 27.3271L25.9354 29.5602C25.3577 31.5332 23.5481 32.8889 21.4923 32.8889L13.5732 32.8889L10.7499 31.3573L7.69336 32.8889H8.60461C10.6663 32.8889 12.4796 34.2522 13.0521 36.2329L14.3307 40.656Z"
        fill="url(#paint1_radial_56201_15503)"
      />
      <path
        d="M29.4993 4H13.46C8.87732 4 6.12772 10.0566 4.29466 16.1132C2.12296 23.2886 -0.718769 32.8852 7.50252 32.8852H14.4282C16.4978 32.8852 18.3147 31.5168 18.8835 29.5269C20.0876 25.3143 22.1978 17.9655 23.8554 12.3712C24.6977 9.52831 25.3993 7.08673 26.4762 5.56628C27.0799 4.71385 28.086 4 29.4993 4Z"
        fill="url(#paint2_linear_56201_15503)"
      />
      <path
        d="M29.4993 4H13.46C8.87732 4 6.12772 10.0566 4.29466 16.1132C2.12296 23.2886 -0.718769 32.8852 7.50252 32.8852H14.4282C16.4978 32.8852 18.3147 31.5168 18.8835 29.5269C20.0876 25.3143 22.1978 17.9655 23.8554 12.3712C24.6977 9.52831 25.3993 7.08673 26.4762 5.56628C27.0799 4.71385 28.086 4 29.4993 4Z"
        fill="url(#paint3_linear_56201_15503)"
      />
      <path
        d="M18.498 44H34.5374C39.12 44 41.8696 37.9424 43.7027 31.8848C45.8744 24.7081 48.7161 15.1098 40.4948 15.1098H33.5693C31.4996 15.1098 29.6827 16.4784 29.114 18.4684C27.9098 22.6817 25.7996 30.032 24.142 35.6273C23.2996 38.4708 22.598 40.9127 21.5212 42.4335C20.9175 43.286 19.9113 44 18.498 44Z"
        fill="url(#paint4_radial_56201_15503)"
      />
      <path
        d="M18.498 44H34.5374C39.12 44 41.8696 37.9424 43.7027 31.8848C45.8744 24.7081 48.7161 15.1098 40.4948 15.1098H33.5693C31.4996 15.1098 29.6827 16.4784 29.114 18.4684C27.9098 22.6817 25.7996 30.032 24.142 35.6273C23.2996 38.4708 22.598 40.9127 21.5212 42.4335C20.9175 43.286 19.9113 44 18.498 44Z"
        fill="url(#paint5_linear_56201_15503)"
      />
      <defs>
        <radialGradient
          id="paint0_radial_56201_15503"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(38.005 20.5144) rotate(-129.304) scale(17.3033 16.2706)"
        >
          <stop offset="0.0955758" stopColor="#00AEFF" />
          <stop offset="0.773185" stopColor="#2253CE" />
          <stop offset="1" stopColor="#0736C4" />
        </radialGradient>
        <radialGradient
          id="paint1_radial_56201_15503"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(11.1215 32.8171) rotate(51.84) scale(15.9912 15.5119)"
        >
          <stop stopColor="#FFB657" />
          <stop offset="0.633728" stopColor="#FF5F3D" />
          <stop offset="0.923392" stopColor="#C02B3C" />
        </radialGradient>
        <linearGradient
          id="paint2_linear_56201_15503"
          x1="12.5"
          y1="7.5"
          x2="14.7884"
          y2="33.9751"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.156162" stopColor="#0D91E1" />
          <stop offset="0.487484" stopColor="#52B471" />
          <stop offset="0.652394" stopColor="#98BD42" />
          <stop offset="0.937361" stopColor="#FFC800" />
        </linearGradient>
        <linearGradient
          id="paint3_linear_56201_15503"
          x1="14.5"
          y1="4"
          x2="15.7496"
          y2="32.8852"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#3DCBFF" />
          <stop offset="0.246674" stopColor="#0588F7" stopOpacity="0" />
        </linearGradient>
        <radialGradient
          id="paint4_radial_56201_15503"
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(41.3187 12.2813) rotate(109.274) scale(38.3873 45.9867)"
        >
          <stop offset="0.0661714" stopColor="#8C48FF" />
          <stop offset="0.5" stopColor="#F2598A" />
          <stop offset="0.895833" stopColor="#FFB152" />
        </radialGradient>
        <linearGradient
          id="paint5_linear_56201_15503"
          x1="42.5859"
          y1="13.346"
          x2="42.5695"
          y2="21.2147"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0581535" stopColor="#F8ADFA" />
          <stop offset="0.708063" stopColor="#A86EDD" stopOpacity="0" />
        </linearGradient>
      </defs>
    </svg>
  );
}

function Snowflake() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width="800px"
      height="800px"
      viewBox="0 0 512 512"
      aria-hidden="true"
      role="img"
      preserveAspectRatio="xMidYMid meet"
    >
      <path
        fill="#59CAFC"
        d="M476.674 280.72l-118.887 14.006L288 254.435l69.787-40.291l118.887 14.007c.655.077 1.307.115 1.951.115c8.25 0 15.377-6.176 16.366-14.571c1.066-9.05-5.406-17.251-14.456-18.317l-74.949-8.83l46.691-26.957c7.892-4.557 10.596-14.648 6.039-22.54c-4.556-7.892-14.646-10.597-22.539-6.04l-46.692 26.957l29.827-69.323c3.602-8.371-.264-18.076-8.635-21.678c-8.368-3.601-18.076.264-21.678 8.635l-47.313 109.963l-68.363 39.47V144.45l71.572-95.955c5.449-7.305 3.944-17.643-3.36-23.091c-7.305-5.448-17.643-3.944-23.091 3.361l-45.121 60.492V40.892c0-9.113-7.388-16.5-16.5-16.5s-16.5 7.387-16.5 16.5v48.366l-45.122-60.493c-5.449-7.305-15.788-8.808-23.091-3.361c-7.305 5.449-8.809 15.787-3.361 23.091l71.574 95.956v82.226l-71.211-41.114l-47.313-109.961c-3.602-8.37-13.307-12.236-21.678-8.635c-8.371 3.602-12.237 13.308-8.635 21.678l29.828 69.323l-41.886-24.183c-7.892-4.555-17.983-1.852-22.54 6.04c-4.556 7.892-1.853 17.983 6.04 22.54l41.886 24.183l-74.95 8.83c-9.05 1.066-15.522 9.267-14.456 18.317c.989 8.395 8.116 14.572 16.366 14.571c.644 0 1.296-.038 1.951-.115l118.888-14.007L222 254.434l-69.787 40.292L33.326 280.72c-9.053-1.065-17.251 5.405-18.317 14.456c-1.066 9.05 5.406 17.251 14.456 18.317l74.949 8.83l-41.885 24.182c-7.892 4.556-10.596 14.647-6.04 22.539c3.056 5.294 8.602 8.253 14.305 8.253c2.799 0 5.636-.714 8.234-2.214l41.887-24.183l-29.829 69.324c-3.602 8.371.265 18.076 8.635 21.678a16.44 16.44 0 0 0 6.514 1.348c6.393 0 12.478-3.737 15.165-9.982l47.314-109.963l71.21-41.113v80.583l-71.574 95.956c-5.448 7.305-3.944 17.643 3.361 23.091a16.418 16.418 0 0 0 9.853 3.275c5.031 0 9.999-2.293 13.239-6.636l45.122-60.493v53.915c0 9.112 7.387 16.5 16.5 16.5s16.5-7.388 16.5-16.5v-53.914l45.121 60.492c5.448 7.306 15.788 8.809 23.091 3.36c7.305-5.448 8.81-15.786 3.36-23.091l-71.572-95.955v-78.941l68.363 39.469l47.314 109.963c2.687 6.245 8.771 9.982 15.164 9.982a16.44 16.44 0 0 0 6.514-1.348c8.371-3.602 12.236-13.307 8.635-21.678l-29.828-69.324l46.692 26.958a16.42 16.42 0 0 0 8.234 2.214c5.702 0 11.249-2.96 14.305-8.253c4.557-7.892 1.853-17.983-6.039-22.539l-46.69-26.957l74.948-8.83c9.05-1.066 15.522-9.268 14.456-18.317c-1.068-9.047-9.262-15.52-18.319-14.454z"
      />
    </svg>
  );
}

function PowerBI() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <g fill="#F2C811">
        <rect x="3" y="13" width="4.5" height="8" rx="1" />
        <rect x="9.7" y="8" width="4.5" height="13" rx="1" />
        <rect x="16.5" y="3" width="4.5" height="18" rx="1" />
      </g>
    </svg>
  );
}

/**
 * Site do fornecedor.
 *
 * Era uma seta saindo de uma caixa. A seta diz "abre em outro lugar" — o que
 * TODOS os destinos fazem, então ela não distinguia nada; ficava ao lado do
 * Teams e do Snowflake dizendo o óbvio.
 *
 * O globo diz o que este destino É: um site de fora. `currentColor` porque
 * este é o único que não é marca de ninguém, e acompanhar a cor do botão faz
 * ele assentar no desenho em vez de brigar com ele.
 */
function External() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3.3 9.5h17.4M3.3 14.5h17.4" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </svg>
  );
}

const MARKS = {
  teams: Teams,
  m365: Microsoft365,
  snowflake: Snowflake,
  powerbi: PowerBI,
  externo: External,
};

/**
 * @param {object} p
 * @param {string} p.url  o endereço; o ícone sai dele
 */
export function DestinationIcon({ url }) {
  const Mark = MARKS[destinationOf(url)] ?? External;
  return (
    <span className="dest-icon" aria-hidden="true">
      <Mark />
    </span>
  );
}

export default DestinationIcon;
